import { ADMIN } from "@/constants/testIds";

function fmt(n, digits = 2) {
  if (typeof n !== "number" || Number.isNaN(n)) return "—";
  return n.toLocaleString("en-MY", { minimumFractionDigits: digits, maximumFractionDigits: digits });
}

/**
 * mode: "avg" | "total"
 * state_kpis: [{name, n_bills, n_users, avg_kwh, total_kwh, avg_cost_rm, total_cost_rm,
 *              avg_cost_per_kwh, total_cost_per_kwh, load_factor, avg_co2_kg, total_co2_kg}]
 * district_kpis: same shape + state
 */
export default function BreakdownSection({ mode, state_kpis = [], district_kpis = [] }) {
  const kwhKey = mode === "avg" ? "avg_kwh" : "total_kwh";
  const costKey = mode === "avg" ? "avg_cost_rm" : "total_cost_rm";
  const cpkKey = mode === "avg" ? "avg_cost_per_kwh" : "total_cost_per_kwh";
  const co2Key = mode === "avg" ? "avg_co2_kg" : "total_co2_kg";
  const label = mode === "avg" ? "averages" : "totals";

  return (
    <div data-testid={ADMIN.breakdownSection} className="space-y-4">
      <BreakdownTable
        title="By state"
        subtitle={`Every KPI, grouped by Malaysian state (${label}).`}
        testid={ADMIN.stateTable}
        rowTestid={(name) => ADMIN.stateKpiRow(name)}
        rows={state_kpis}
        columns={[
          { key: "name", label: "State", type: "name" },
          { key: "n_users", label: "Users", type: "int" },
          { key: "n_bills", label: "Bills", type: "int" },
          { key: kwhKey, label: "kWh", digits: 0 },
          { key: costKey, label: "RM", digits: 2 },
          { key: cpkKey, label: "RM/kWh", digits: 4 },
          { key: "avg_daily_kwh", label: "Avg daily kWh", digits: 2 },
          { key: "load_factor", label: "Load factor", digits: 3 },
          { key: co2Key, label: "CO₂ kg", digits: 1 },
        ]}
      />

      <BreakdownTable
        title="By district"
        subtitle={`Every KPI, grouped by daerah/district (${label}).`}
        testid={ADMIN.districtTable}
        rowTestid={(name) => ADMIN.districtKpiRow(name)}
        rows={district_kpis}
        columns={[
          { key: "name", label: "District", type: "name" },
          { key: "state", label: "State", type: "text" },
          { key: "n_users", label: "Users", type: "int" },
          { key: "n_bills", label: "Bills", type: "int" },
          { key: kwhKey, label: "kWh", digits: 0 },
          { key: costKey, label: "RM", digits: 2 },
          { key: cpkKey, label: "RM/kWh", digits: 4 },
          { key: "avg_daily_kwh", label: "Avg daily kWh", digits: 2 },
          { key: "load_factor", label: "Load factor", digits: 3 },
          { key: co2Key, label: "CO₂ kg", digits: 1 },
        ]}
      />
    </div>
  );
}

function BreakdownTable({ title, subtitle, rows, columns, testid, rowTestid }) {
  return (
    <div data-testid={testid} className="flat-card overflow-hidden">
      <div className="p-6 sm:p-8 border-b border-[#E5E5DE]">
        <div className="overline mb-2">Geography · Breakdown</div>
        <h3 className="font-display text-xl tracking-tight">{title}</h3>
        <p className="text-sm text-[#6E716C] mt-1">{subtitle}</p>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="bg-[#F7F7F4]">
              {columns.map((c) => (
                <th
                  key={c.key}
                  className="text-left font-mono-data uppercase tracking-widest text-[10px] text-[#6E716C] px-4 sm:px-6 py-3 whitespace-nowrap"
                >
                  {c.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 ? (
              <tr>
                <td colSpan={columns.length} className="px-6 py-8 text-center font-mono-data text-sm text-[#6E716C]">
                  No location data yet — upload TNB PDFs so users' cities/districts/states can be extracted.
                </td>
              </tr>
            ) : rows.map((r) => (
              <tr
                key={r.name}
                data-testid={rowTestid?.(r.name)}
                className="border-b border-[#E5E5DE] hover:bg-[#FAFAF7] transition-colors"
              >
                {columns.map((c) => {
                  const v = r[c.key];
                  if (c.type === "name") {
                    return (
                      <td key={c.key} className="px-4 sm:px-6 py-4 font-display text-base tracking-tight">
                        {v || "—"}
                      </td>
                    );
                  }
                  if (c.type === "text") {
                    return (
                      <td key={c.key} className="px-4 sm:px-6 py-4 font-mono-data text-xs text-[#6E716C]">
                        {v || "—"}
                      </td>
                    );
                  }
                  if (c.type === "int") {
                    return (
                      <td key={c.key} className="px-4 sm:px-6 py-4 font-mono-data text-sm">
                        {typeof v === "number" ? v : "—"}
                      </td>
                    );
                  }
                  return (
                    <td key={c.key} className="px-4 sm:px-6 py-4 font-mono-data text-sm">
                      {typeof v === "number" ? fmt(v, c.digits) : "—"}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
